/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';

import {
  ReactElement,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { thumbnailStyles } from 'components/shared/twin.styles';
import { ILibraryItem } from 'utils/models';
import { SEMANTIC_FILTER_ONLY } from '../semanticSearchMapper';
import {
  canHoverPlay,
  createHoverPlayback,
  fetchPreviewUrl,
  isAudioItem,
  resolveSemanticPlayWindow,
} from './semanticPlayback';

type SemanticHoverPlay = {
  /** null when the item cannot hover-play (see canHoverPlay). */
  mediaElement: ReactElement | null;
  isPlaying: boolean;
  isAudio: boolean;
  onEnter: () => void;
  onLeave: () => void;
};

/** Plays a semantic result's matched passage, with sound, while its thumbnail
 * is hovered. Timer, URL cache and stale-hover guard live in
 * createHoverPlayback / fetchPreviewUrl. */
export const useSemanticHoverPlay = (item: ILibraryItem): SemanticHoverPlay => {
  const queryClient = useQueryClient();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playback] = useState(createHoverPlayback);
  // A plain holder, not useRef: the unmount cleanup must read the element that
  // is mounted at that time, and an <audio> left playing after unmount keeps
  // making sound.
  const [media] = useState<{ el: HTMLMediaElement | null }>(() => ({
    el: null,
  }));

  const { mediaid } = item;
  const isFilterOnlySearch = useSelector(
    (state: RootState) =>
      state.library.data?.semanticQueryType === SEMANTIC_FILTER_ONLY,
  );
  const enabled = canHoverPlay(item);
  const playWindow = resolveSemanticPlayWindow(item, isFilterOnlySearch);
  const isAudio = isAudioItem(item);

  const setMediaRef = useCallback(
    (el: HTMLMediaElement | null) => {
      media.el = el;
    },
    [media],
  );

  // Unmount also covers a click that navigates to the transcript.
  useEffect(() => () => playback.release(media.el), [playback, media]);

  const onEnter = () => {
    if (!enabled || !playWindow) return;

    playback.enter({
      loadUrl: () => fetchPreviewUrl(queryClient, mediaid),
      getElement: () => media.el,
      startSec: playWindow.startSec,
      endSec: playWindow.endSec,
      onWarn: (reason) => console.warn(`[library] ${reason}`, mediaid),
    });
  };

  const onLeave = () => {
    playback.leave(media.el);
    setIsPlaying(false);
  };

  const handleTimeUpdate = (e: SyntheticEvent<HTMLMediaElement>) => {
    if (playWindow && e.currentTarget.currentTime >= playWindow.endSec) {
      e.currentTarget.pause();
    }
  };

  const mediaProps = {
    ref: setMediaRef,
    preload: 'none',
    onPlaying: () => setIsPlaying(true),
    onPause: () => setIsPlaying(false),
    onEnded: () => setIsPlaying(false),
    onTimeUpdate: handleTimeUpdate,
  };

  let mediaElement: ReactElement | null = null;
  if (enabled) {
    mediaElement = isAudio ? (
      <audio {...mediaProps} />
    ) : (
      <video
        {...mediaProps}
        playsInline
        controls={false}
        controlsList="nodownload"
        css={[
          isPlaying ? tw`block` : tw`hidden`,
          thumbnailStyles,
          tw`pointer-events-none`, // prevent right-click hack
        ]}
      />
    );
  }

  return { mediaElement, isPlaying, isAudio, onEnter, onLeave };
};
